/**
 * Payment labels shared by the admin order screens and the customer's order
 * history, so a payment is never described two different ways depending on who
 * is looking at it.
 *
 * Two independent fields decide the wording:
 *   paymentMethod     — how the order was placed: cod, razorpay, wallet,
 *                       wallet+razorpay
 *   paymentInstrument — what the money actually moved on: cash, upi, card,
 *                       netbanking, wallet, emi
 *
 * The label is derived from that pair rather than enumerating the valid
 * combinations, so a new instrument reads correctly without anyone editing this
 * file. That generality is what the doorstep-collection case needed: 'cod' now
 * covers both cash handed over and a UPI QR scan, and the instrument is the
 * only thing that separates them.
 */

/**
 * The order.paymentMethod values the backend sends, mirroring OrderPaymentMethod
 * in backend/src/orders/order.entity.ts. Every comparison and map key in the
 * frontend goes through these, so a rename is a compile-time-ish rename here
 * rather than a hunt for quoted strings across screens.
 *
 * Mirroring, not importing — the two codebases ship separately, so this list can
 * lag the backend's. Nothing here may assume it is exhaustive: the column is a
 * varchar the backend can write a new value into before this file learns about
 * it, and both label and colour lookups below fall through deliberately rather
 * than assuming the value must be one of these four.
 */

import { round2 } from './money';

export const PAYMENT_METHODS = {
  COD: 'cod',
  RAZORPAY: 'razorpay',
  WALLET: 'wallet',
  WALLET_RAZORPAY: 'wallet+razorpay',
};

/**
 * The order.paymentStatus values in which the customer's money is still held
 * against the order, so a cancellation owes them a refund. Mirrors
 * MONEY_HELD_PAYMENT_STATUSES in backend/src/orders/order.entity.ts.
 *
 * 'partial_refund' belongs here (BMF-159). An amendment returns only the
 * reduced difference and leaves the *revised* total still paid — a ₹210 order
 * cut to ₹160 is every bit as paid as before, just for less. Screens that read
 * `paymentStatus === 'paid'` as "money was collected" therefore treat every
 * amended order as unpaid, which is how the cancel dialog came to tell a
 * customer holding a refundable balance that "no payment has been collected for
 * this order, cancellation is free".
 *
 * Deliberately excludes 'refunded' / 'refund_requested': the money is already
 * on its way back.
 */
export const MONEY_HELD_PAYMENT_STATUSES = ['paid', 'partial_refund'];

/** True when the customer still has money sitting against this order. */
export const isMoneyHeld = (order = {}) =>
  MONEY_HELD_PAYMENT_STATUSES.includes(order.paymentStatus);

/**
 * After a partial-cancellation amendment, one leg of a split order can be fully
 * refunded to zero — e.g. the whole online portion is returned, leaving only BMF
 * Coins. The stored payment_method stays 'wallet+razorpay', but the order is no
 * longer split, so labels must reflect the leg that remains. Returns:
 *   'wallet' — only coins remain (online refunded to 0)
 *   'online' — only online remains (coins refunded to 0)
 *   'split'  — both legs still present, or the amounts aren't known
 * round2 (not Math.round) so a real ₹0.40 remainder isn't treated as zero.
 */
export const splitRemainingLeg = (order = {}) => {
  if (order.walletAmount == null || order.onlineAmount == null) return 'split';
  const wallet = round2(order.walletAmount);
  const online = round2(order.onlineAmount);
  if (online === 0 && wallet > 0) return 'wallet';
  if (wallet === 0 && online > 0) return 'online';
  return 'split';
};

export const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHODS.WALLET]: 'BMF Coins',
  [PAYMENT_METHODS.WALLET_RAZORPAY]: 'Split Payment (BMF Coins + Online Payment)',
  [PAYMENT_METHODS.RAZORPAY]: 'Online Payment',
  [PAYMENT_METHODS.COD]: 'Cash on Delivery',
};

/**
 * The method written out on its own, for the places that give the instrument a
 * column of its own and would only repeat it in a suffix.
 *
 * Falls back to the raw method before giving up, never to a specific one. A
 * method with no entry above — a new one the backend starts sending — would
 * otherwise be announced as "Cash on Delivery", claiming money is still owed at
 * the door on an order that may well be prepaid. Showing the raw value is less
 * polished and cannot mislead.
 */
export const getMethodName = (method) =>
  PAYMENT_METHOD_LABELS[method] || method || 'Unknown Payment';

/** Instrument codes written out for a human ('netbanking' → 'Net Banking'). */
export const getInstrumentName = (instrument) => {
  if (!instrument) return '';
  const lower = instrument.toLowerCase();
  if (lower === 'upi') return 'UPI';
  if (lower === 'netbanking') return 'Net Banking';
  return instrument.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * The full label, e.g. "Cash on Delivery (UPI)" or "Online Payment (Card)".
 *
 * An order with no instrument yet — placed but not paid — is named by its
 * method alone rather than being guessed at.
 */
export const formatPaymentMethod = (order = {}) => {
  const { paymentMethod: method, paymentInstrument: instrument } = order;
  const instrumentName = getInstrumentName(instrument);

  // Split payments name the two amounts, so they compose their own string
  // rather than taking the single-instrument shape below.
  if (method === PAYMENT_METHODS.WALLET_RAZORPAY) {
    const walletAmt = order.walletAmount != null
      ? `₹${Math.round(order.walletAmount)} BMF Coins`
      : 'BMF Coins';
    const onlineAmt = order.onlineAmount != null
      ? `₹${Math.round(order.onlineAmount)} ${instrumentName || 'Online'}`
      : `${instrumentName || 'Online'} Payment`;

    // A partial cancellation can leave only one leg (see splitRemainingLeg). A
    // "Split Payment (… + ₹0 …)" then misdescribes the order — collapse to the
    // single remaining leg with its amount. splitRemainingLeg uses round2 (not
    // Math.round) for the zero-check, so a real ₹0.40 remainder isn't treated as
    // fully refunded. (The display strings above keep Math.round — whole rupees.)
    const leg = splitRemainingLeg(order);
    if (leg === 'wallet') return walletAmt;   // coins-only, e.g. "₹150 BMF Coins"
    if (leg === 'online') return onlineAmt;   // online-only, e.g. "₹75 UPI"

    return `Split Payment (${walletAmt} + ${onlineAmt})`;
  }

  // An instrument that merely restates the method carries no information —
  // "BMF Coins (Wallet)" is noise — so the suffix is dropped. Compared as a
  // general rule rather than by naming the pair, so it holds for any future
  // method whose instrument shares its name.
  const restatesMethod =
    instrument && method && instrument.toLowerCase() === method.toLowerCase();

  const fallback = getMethodName(method);

  if (!instrumentName || restatesMethod) return fallback;
  return `${fallback} (${instrumentName})`;
};

/** Compact names for the admin orders table, where the full label will not fit. */
export const METHOD_SHORT_LABELS = {
  [PAYMENT_METHODS.COD]: 'COD',
  [PAYMENT_METHODS.WALLET]: 'Coins',
  [PAYMENT_METHODS.WALLET_RAZORPAY]: 'Coins + Online',
  [PAYMENT_METHODS.RAZORPAY]: 'Online',
};

/**
 * Compact form for the admin orders table, where the full label will not fit in
 * a pill.
 *
 * Each method gets its own short name. Treating "not COD" as a synonym for
 * "Online" — as this used to — announced an order paid entirely from BMF Coins
 * as an online payment. That is not a small imprecision on a screen an admin
 * reconciles the day's takings from: coins never left the platform and no money
 * arrived at the gateway, so an order counted as online is one that will not be
 * found in the Razorpay settlement.
 *
 * COD keeps its instrument, because whether an agent is carrying cash for that
 * order is the thing worth seeing without opening it. The prepaid methods drop
 * theirs — card vs UPI is detail for the order view, and the pill has no room.
 *
 * An unrecognised method falls back to its raw value for the reason given on
 * getMethodName: naming it after any specific method would state something
 * about the money that may not be true.
 */
export const formatMethodShort = (order = {}) => {
  const method = order.paymentMethod;

  // A split order that a partial cancellation reduced to a single leg is no
  // longer "Coins + Online" — show just the leg that remains, so the admin list
  // matches the actual (post-amendment) payment. Mirrors formatPaymentMethod.
  if (method === PAYMENT_METHODS.WALLET_RAZORPAY) {
    const leg = splitRemainingLeg(order);
    if (leg === 'wallet') return METHOD_SHORT_LABELS[PAYMENT_METHODS.WALLET]; // 'Coins'
    if (leg === 'online') return METHOD_SHORT_LABELS[PAYMENT_METHODS.RAZORPAY]; // 'Online'
  }

  const short = METHOD_SHORT_LABELS[method] || method || 'Unknown';

  if (method === PAYMENT_METHODS.COD) {
    const instrumentName = getInstrumentName(order.paymentInstrument);
    return instrumentName ? `${short} (${instrumentName})` : short;
  }

  return short;
};
